import type { ReactNode } from "react";

import type { OrganizationProjection } from "@/api/adminApi";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import { PageHeader, Pill } from "./shared";

export type OrganizationWorkspaceTab = "overview" | "publication-readiness" | "users-access";

const WORKSPACE_TABS: Array<{ id: OrganizationWorkspaceTab; label: string }> = [
  { id: "overview", label: "Overview" },
  { id: "publication-readiness", label: "Publication readiness" },
  { id: "users-access", label: "Users & access" },
];

export function OrganizationWorkspaceSection({
  organization,
  activeProgramCount,
  staffCount,
  activeTab,
  onTabChange,
  onBackToList,
  publicationReadiness,
  usersAccess,
}: {
  organization: OrganizationProjection;
  activeProgramCount: number;
  staffCount: number;
  activeTab: OrganizationWorkspaceTab;
  onTabChange: (tab: OrganizationWorkspaceTab) => void;
  onBackToList: () => void;
  publicationReadiness: ReactNode;
  usersAccess: ReactNode;
}) {
  return (
    <>
      <PageHeader
        title={organization.name}
        subtitle={`${organization.slug} · ${organization.status}`}
        action={
          <Button type="button" variant="outline" size="sm" onClick={onBackToList}>
            Back to organizations
          </Button>
        }
      />
      <div className="space-y-4 p-4">
        <nav
          aria-label="Organization workspace sections"
          className="flex flex-wrap gap-2 border-b pb-3"
        >
          {WORKSPACE_TABS.map((tab) => (
            <Button
              key={tab.id}
              type="button"
              variant={activeTab === tab.id ? "default" : "outline"}
              size="sm"
              onClick={() => onTabChange(tab.id)}
            >
              {tab.label}
            </Button>
          ))}
        </nav>

        {activeTab === "overview" ? (
          <OrganizationOverview
            organization={organization}
            activeProgramCount={activeProgramCount}
            staffCount={staffCount}
          />
        ) : null}
        {activeTab === "publication-readiness" ? publicationReadiness : null}
        {activeTab === "users-access" ? usersAccess : null}
      </div>
    </>
  );
}

function OrganizationOverview({
  organization,
  activeProgramCount,
  staffCount,
}: {
  organization: OrganizationProjection;
  activeProgramCount: number;
  staffCount: number;
}) {
  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
      <Card>
        <CardContent className="p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Status
          </div>
          <div className="mt-2">
            <Pill tone={organization.is_active ? "green" : "slate"}>
              {organization.status}
            </Pill>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Active programs
          </div>
          <div className="mt-1 text-2xl font-semibold text-gray-950">
            {activeProgramCount}
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Staff users
          </div>
          <div className="mt-1 text-2xl font-semibold text-gray-950">{staffCount}</div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Primary admin
          </div>
          <div className="mt-1 text-sm font-medium text-gray-950">
            {organization.primary_admin ?? "Not assigned"}
          </div>
          <div className="mt-1 text-xs text-gray-500">
            {organization.recent_activity ?? "No recent activity"}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
